import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Coins, Award, ScrollText, Landmark, Swords } from "lucide-react";
import { SKILL_LABEL } from "@/lib/gameData";
import { useGame } from "@/context/GameContext";
import { flagUrl } from "@/lib/utils";
import { useOpenPlayer } from "@/components/game/PlayerCard";

const CATEGORIES = [
  { id: "total_xp",     label: "Total XP", icon: Trophy },
  { id: "gold",         label: "Gold",     icon: Coins },
  { id: "quests",       label: "Quests",   icon: ScrollText },
  { id: "achievements", label: "Achievements", icon: Award },
];

// Country-level metrics — spec: Wealth, Damage, Regions held.
const COUNTRY_CATEGORIES = [
  { id: "country_wealth",  label: "Wealth",  icon: Coins },
  { id: "country_damage",  label: "Damage",  icon: Swords },
  { id: "country_regions", label: "Regions", icon: Landmark },
];

// Every trainable skill in one flat list — leaderboard groups them together
// under a single "Skills" header instead of splitting by Combat / Gathering /
// Artisan & Support.
const ALL_SKILLS_FLAT = [
  "attack", "strength", "defence", "hitpoints", "ranged", "magic", "prayer", "slayer",
  "woodcutting", "mining", "fishing", "farming", "hunter",
  "cooking", "firemaking", "smithing", "fletching", "crafting",
  "herblore", "runecrafting", "agility", "thieving", "construction",
];

export function LeaderboardPanel() {
  const { content, snap } = useGame();
  const openPlayer = useOpenPlayer();
  const [category, setCategory] = useState("total_xp");
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("player");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (cat) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/game/social/leaderboards?category=${cat}`);
      setRows(data.rows || []);
      setKind(data.kind || "player");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(category); }, [category, refresh]);
  // Live refresh every 15s so the boards feel alive without hammering the server.
  useEffect(() => {
    const iv = setInterval(() => refresh(category), 15000);
    return () => clearInterval(iv);
  }, [category, refresh]);

  const myName = snap?.name;
  const myIso  = snap?.home_country_iso;
  const myRankIdx = useMemo(() => {
    if (kind === "country") return rows.findIndex((r) => r.home_country_iso === myIso);
    return rows.findIndex((r) => r.name === myName);
  }, [rows, kind, myName, myIso]);

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="leaderboard-panel">
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <Trophy size={28} className="text-amber-400 sm:hidden" />
            <Trophy size={34} className="text-amber-400 hidden sm:block" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-4xl heading-gilded truncate">Leaderboards</h1>
            <p className="text-stone-400 font-body italic text-xs sm:text-sm">
              The Great Ledger keeps tally. Where do you stand?
            </p>
            {myRankIdx >= 0 && (
              <div className="mt-1 sm:mt-2 text-xs sm:text-sm font-numeric text-stone-300">
                Your rank · <span className="text-amber-300">#{myRankIdx + 1}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category selector */}
      <div className="panel p-3 space-y-2">
        <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">Overall</h3>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => (
            <CategoryChip key={c.id} category={c} active={category === c.id} onClick={() => setCategory(c.id)} />
          ))}
        </div>
        <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mt-2">Nations</h3>
        <div className="flex flex-wrap gap-1.5">
          {COUNTRY_CATEGORIES.map((c) => (
            <CategoryChip key={c.id} category={c} active={category === c.id} onClick={() => setCategory(c.id)} />
          ))}
        </div>
        <SkillGroup label="Skills" skills={ALL_SKILLS_FLAT} category={category} setCategory={setCategory} />
      </div>

      {/* Rows */}
      <div className="panel p-3" data-testid="leaderboard-rows">
        <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2">
          {CATEGORIES.find((c) => c.id === category)?.label
            || COUNTRY_CATEGORIES.find((c) => c.id === category)?.label
            || SKILL_LABEL[category] || category}
          {kind === "country" && <span className="ml-2 text-stone-500 normal-case tracking-normal">· nations ranking</span>}
        </h3>
        {loading && rows.length === 0 ? (
          <div className="text-stone-500 italic py-6 text-center">Reading the ledger…</div>
        ) : rows.length === 0 ? (
          <div className="text-stone-500 italic py-6 text-center">No wanderers on this board yet.</div>
        ) : (
          <ol className="space-y-1">
            {rows.map((r, i) => {
              const mine = kind === "country" ? (r.home_country_iso === myIso) : (r.name === myName);
              const rankColor =
                i === 0 ? "text-yellow-300" :
                i === 1 ? "text-stone-300"  :
                i === 2 ? "text-orange-400" : "text-stone-500";
              const clickable = kind !== "country";
              return (
                <li
                  key={r.id}
                  onClick={clickable ? () => openPlayer(r.id) : undefined}
                  className={
                    "flex items-center gap-3 px-2 py-1.5 rounded border transition-colors " +
                    (clickable ? "cursor-pointer " : "") +
                    (mine ? "bg-amber-950/40 border-amber-700"
                          : "border-transparent hover:bg-stone-900/70 hover:border-amber-900")
                  }
                  data-testid={`lb-row-${i}`}
                >
                  <span className={`font-numeric text-sm w-8 text-right ${rankColor}`}>#{i + 1}</span>
                  {kind === "country" ? (
                    <div className="w-8 h-8 rounded bg-stone-950 border border-amber-800 flex items-center justify-center shrink-0 overflow-hidden">
                      {r.home_country_iso_a2 && (
                        <img
                          src={flagUrl(r.home_country_iso_a2, 40)}
                          alt={r.home_country_iso}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-stone-950 border border-amber-800 flex items-center justify-center font-display text-[10px] text-amber-400 shrink-0">
                      {(r.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  {kind !== "country" && r.home_country_iso_a2 && (
                    <img
                      src={flagUrl(r.home_country_iso_a2, 40)}
                      alt={r.home_country_iso || r.home_country_iso_a2}
                      title={r.home_country_name || r.home_country_iso || r.home_country_iso_a2}
                      loading="lazy"
                      className="w-5 h-3.5 object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.6)] shrink-0"
                      data-testid={`lb-row-${i}-flag`}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <span className="flex-1 font-body text-sm text-stone-100 truncate">{r.name}</span>
                  {kind === "country" && (
                    <span className="text-[10px] font-numeric text-stone-500 shrink-0">{r.member_count} m</span>
                  )}
                  <span className="font-numeric text-xs text-amber-300 shrink-0">{r.metric_label}</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

function CategoryChip({ category, active, onClick }) {
  const Icon = category.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`lb-cat-${category.id}`}
      className={
        "inline-flex items-center gap-1.5 text-xs font-body px-2.5 py-1 rounded border transition-colors " +
        (active
          ? "bg-amber-800/50 border-amber-500 text-amber-100"
          : "bg-stone-900 border-stone-700 text-stone-300 hover:border-amber-700 hover:text-amber-200")
      }
    >
      <Icon size={12} /> {category.label}
    </button>
  );
}

function SkillGroup({ label, skills, category, setCategory }) {
  return (
    <div>
      <h4 className="uppercase tracking-widest text-[9px] font-numeric text-stone-500 mt-1 mb-1">{label}</h4>
      <div className="flex flex-wrap gap-1.5">
        {skills.map((s) => (
          <button
            key={s}
            onClick={() => setCategory(s)}
            data-testid={`lb-cat-${s}`}
            className={
              "text-[11px] font-body px-2 py-0.5 rounded border transition-colors " +
              (category === s
                ? "bg-amber-800/50 border-amber-500 text-amber-100"
                : "bg-stone-900 border-stone-700 text-stone-400 hover:border-amber-700 hover:text-amber-200")
            }
          >
            {SKILL_LABEL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
